/**
 * 管理员页面初始化逻辑 Hook
 * 处理页面加载时的初始化逻辑
 */
import { useEffect, useRef } from "react";

export interface UseAdminInitializationProps {
  handleSearch: () => Promise<void>;
  adminList: any[];
  loadPermissionCounts: (adminIds: string[]) => void;
}

export const useAdminInitialization = ({
  handleSearch,
  adminList,
  loadPermissionCounts,
}: UseAdminInitializationProps) => {
  // 使用 ref 标记是否已初始化，避免重复执行
  const initializedRef = useRef(false);

  // 初始化加载数据（只在组件挂载时执行一次）
  useEffect(() => {
    if (initializedRef.current) return;

    // 清除可能导致描述列隐藏的缓存配置
    try {
      const storageKey = "admin_management_table";
      const columnVisibilityKey = `table_columns_${storageKey}`;
      const savedConfig = localStorage.getItem(columnVisibilityKey);

      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig);
        // 确保描述列可见
        if (parsedConfig["description"] === false) {
          parsedConfig["description"] = true;
          localStorage.setItem(columnVisibilityKey, JSON.stringify(parsedConfig));
        }
      }
    } catch (error) {
      // 静默处理错误
    }

    handleSearch();
    initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在挂载时执行一次

  // 当管理员列表更新时，异步加载权限数量
  // 使用 adminList 的 ID 数组作为依赖，避免引用变化导致的循环
  const adminIdsRef = useRef<string>("");

  useEffect(() => {
    if (adminList.length > 0) {
      const adminIds = adminList.map((admin) => admin.id).sort().join(",");
      // 只有当 ID 列表真正改变时才重新加载
      if (adminIdsRef.current !== adminIds) {
        adminIdsRef.current = adminIds;
        const adminIdArray = adminList.map((admin) => admin.id);
        loadPermissionCounts(adminIdArray);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminList]); // 依赖 adminList，但通过 ref 比较避免重复加载
};

